import {
  BanknoteIcon,
  ClapperboardIcon,
  ClipboardCheckIcon,
  FileSignatureIcon,
  HourglassIcon,
  MegaphoneIcon,
  ScrollTextIcon,
  ZapIcon,
  type LucideIcon,
} from "lucide-react";
import { EvePipelinePhase } from "@/types/eve";
import { PartnershipTier } from "@/types/types";
import { currencySymbol } from "@/lib/format";

/** The phase's ENTRY status — what a creator shows when they land in it. */
export function entryStatusFor(phase: EvePipelinePhase): string {
  switch (phase.key) {
    case "contract":
      return "pending-contract";
    case "payment_1":
      return "pending-payment-1";
    case "payment_2":
      return "pending-payment-2";
    case "script":
      return "waiting-script";
    case "draft":
      return "waiting-draft";
    case "posting":
      return "waiting-posting";
    case "payment":
      return "pending-payment";
  }
  if (phase.shape === "action") return `${phase.key}-run`;
  if (phase.shape === "wait") return `${phase.key}-wait`;
  return `waiting-${phase.key}`;
}

export function phaseIcon(phase: EvePipelinePhase): LucideIcon {
  switch (phase.key) {
    case "contract":
      return FileSignatureIcon;
    case "script":
      return ScrollTextIcon;
    case "draft":
      return ClapperboardIcon;
    case "posting":
      return MegaphoneIcon;
    case "payment":
    case "payment_1":
    case "payment_2":
      return BanknoteIcon;
  }
  if (phase.shape === "action") return ZapIcon;
  if (phase.shape === "wait") return HourglassIcon;
  return ClipboardCheckIcon;
}

export function phaseDescription(phase: EvePipelinePhase): string {
  switch (phase.key) {
    case "contract":
      return "E-signature gate between your approval and the activation email — the creator signs before Amy activates them.";
    case "payment_1":
      return "Pays a percent of the agreed fee early (e.g. right after the contract) via Grade. A human always confirms before money moves; the final Payment pays the rest.";
    case "payment_2":
      return "A second early sub-payment: pays another percent of the agreed fee via Grade. A human always confirms before money moves; the final Payment pays the rest.";
    case "script":
      return "The creator submits their script, and it goes through review before they film.";
    case "draft":
      return "The creator submits draft content links. Amy watches the files, and the draft goes through review before posting.";
    case "posting":
      return "Chases the live publish, verifies the posted link is actually live, and feeds the video analytics table.";
    case "payment":
      return "Pays the agreed price via Grade once the work is done — minus whatever the split sub-payments already paid. A human always confirms before money moves.";
  }
  if (phase.shape === "action")
    return "Custom action step: Amy runs this phase's allowlisted tools — the creator is never emailed.";
  if (phase.shape === "wait")
    return "Custom hold: the creator waits here until a verified external event (or a teammate) releases them.";
  return "Custom review step: the creator submits, and the submission goes through review.";
}

/** Phases with a review status — judge mode and the revision cap apply. */
export function phaseHasJudging(phase: EvePipelinePhase): boolean {
  if (phase.key === "script" || phase.key === "draft" || phase.key === "posting") return true;
  return phase.key.startsWith("x-") && phase.shape === "review";
}

/** Action steps and event holds never email the creator — no nudges. */
export function phaseHasNudges(phase: EvePipelinePhase): boolean {
  return phase.shape !== "action" && phase.shape !== "wait";
}

export function formatWait(seconds: number): string {
  const days = seconds / 86400;
  if (days >= 1) return `${Math.round(days * 10) / 10}d`;
  return `${Math.round(seconds / 3600)}h`;
}

/**
 * The tier's offer without a creator ("55% Rev Share", "Paid + CPA $50") —
 * the Tier column's summary minus the per-creator negotiated price.
 */
export function tierOfferLabel(tier: PartnershipTier | undefined, currency?: string): string {
  const symbol = currencySymbol(currency);
  return (tier?.components ?? [])
    .map((c) => {
      switch (c.type) {
        case "max_budget":
        case "custom_formula":
          return "Paid";
        case "max_cpm":
          return c.value != null ? `${symbol}${c.value} CPM` : "Paid CPM";
        case "cpa":
          return c.value ? `CPA ${symbol}${c.value}` : "CPA";
        case "revenue_share":
          return c.value ? `${c.value}% Rev Share` : "Rev Share";
        case "performance_cpm":
          return "Perf CPM";
        default:
          return "Other";
      }
    })
    .join(" + ");
}

/** Custom phases read as "steps" in the UI, built-ins as "phases". */
export function phaseNoun(key: string): "Step" | "Phase" {
  return key.startsWith("x-") ? "Step" : "Phase";
}
